import { utils, write } from '@e965/xlsx';

interface IARExportRow {
  lin: string;
  nsn: string;
  nomenclature: string;
  location: string;
  recordedQty: number;
  inventoriedQty: number | null;
}

const getGain = (recordedQty: number, inventoriedQty: number | null): number => {
  if (inventoriedQty === null) return 0;
  return Math.max(inventoriedQty - recordedQty, 0);
};

const getLoss = (recordedQty: number, inventoriedQty: number | null): number => {
  if (inventoriedQty === null) return 0;
  return Math.max(recordedQty - inventoriedQty, 0);
};

/**
 * Builds an .xlsx Blob from IAR run rows and triggers a file download.
 *
 * Columns:
 *   #, LIN, NSN, Nomenclature, Location,
 *   Recorded Qty, Count #1, Count #2, Count #3,
 *   Inventoried Qty, Gain, Loss
 *
 * The blank "Count" columns support multi-person collaborative counting
 * as described in the work item.
 */
export const exportIarRunToXlsx = (rows: IARExportRow[], runNumber: string): void => {
  const headers = [
    '#',
    'LIN',
    'NSN',
    'Nomenclature',
    'Location',
    'Recorded Qty',
    'Count #1',
    'Count #2',
    'Count #3',
    'Inventoried Qty',
    'Gain',
    'Loss',
  ];

  const dataRows = rows.map((row, index) => [
    index + 1,
    row.lin,
    row.nsn,
    row.nomenclature,
    row.location,
    row.recordedQty,
    '', // Count #1 (blank for manual entry)
    '', // Count #2 (blank for manual entry)
    '', // Count #3 (blank for manual entry)
    row.inventoriedQty ?? '',
    getGain(row.recordedQty, row.inventoriedQty),
    getLoss(row.recordedQty, row.inventoriedQty),
  ]);

  const ws = utils.aoa_to_sheet([headers, ...dataRows]);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'IAR Run');

  const buffer = write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const filename = `${runNumber}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60000);
};
